import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, TrendingUp, DollarSign, Eye, ShoppingCart } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardOverview = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStockAlerts: 0,
    totalValue: 0,
    categories: 0
  });
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch products and stock alerts
      const [productsResponse, alertsResponse] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/stock/alerts`)
      ]);

      const products = productsResponse.data;
      const alerts = alertsResponse.data;

      // Calculate stats
      const totalProducts = products.length;
      const totalValue = products.reduce((sum, product) => {
        const productStock = product.variants.reduce((variantSum, variant) => 
          variantSum + (variant.stock_quantity * product.price), 0);
        return sum + productStock;
      }, 0);

      const categories = [...new Set(products.map(p => p.category))].length;

      setStats({
        totalProducts,
        lowStockAlerts: alerts.length,
        totalValue,
        categories
      });

      setRecentAlerts(alerts.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats.totalProducts,
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Low Stock Alerts',
      value: stats.lowStockAlerts,
      icon: AlertTriangle,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100'
    },
    {
      title: 'Inventory Value',
      value: `$${stats.totalValue.toLocaleString()}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Categories',
      value: stats.categories,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-2">Welcome back! Here's what's happening with your store.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2" data-testid={`stat-${index}`}>
                    {card.value}
                  </p>
                </div>
                <div className={`${card.bgColor} p-3 rounded-lg`}>
                  <Icon className={`w-6 h-6 ${card.color}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Recent Stock Alerts</h2>
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          
          {recentAlerts.length > 0 ? (
            <div className="space-y-4">
              {recentAlerts.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="font-medium text-gray-900">{alert.product_name}</p>
                    <p className="text-sm text-gray-600">Size {alert.size} - {alert.color}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-amber-700">{alert.current_stock} left</p>
                    <p className="text-xs text-gray-500">Threshold: {alert.threshold}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No stock alerts at the moment</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h2>
          
          <div className="space-y-4">
            <button className="w-full flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
              <Package className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium text-gray-900">Add New Product</p>
                <p className="text-sm text-gray-500">Create a new sneaker listing</p>
              </div>
            </button>
            
            <button className="w-full flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-gray-900">Update Inventory</p>
                <p className="text-sm text-gray-500">Bulk update stock levels</p>
              </div>
            </button>
            
            <button 
              onClick={() => window.open('/', '_blank')}
              className="w-full flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
              data-testid="view-store-button"
            >
              <Eye className="w-5 h-5 text-purple-500" />
              <div>
                <p className="font-medium text-gray-900">View Store</p>
                <p className="text-sm text-gray-500">See your store as customers do</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;